import Layout from '../../components/feature/Layout';
import PageBanner from '../../components/feature/PageBanner';
import { useScrollAnimation, useCountUp } from '../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';

const values = [
  { icon: 'ri-shield-check-line', title: 'Integrity', desc: 'We say what we mean and do what we say. Honest pricing, honest timelines, honest communication — always.' },
  { icon: 'ri-trophy-line', title: 'Craftsmanship', desc: 'Every cut, every joint, every finish is done right. We take pride in the details that most people never notice — but we always do.' },
  { icon: 'ri-user-heart-line', title: 'Client Focus', desc: 'Your home is your most valuable asset. We treat every project as if it were our own family\'s home.' },
  { icon: 'ri-leaf-line', title: 'Community', desc: 'Battle Creek is our home. We give back by hiring local, sourcing locally, and building a better Southwest Michigan.' },
];

const milestones = [
  { year: '1992', title: 'Founded by Brian Downs', desc: 'Brian started the company with a single truck, a set of tools, and a commitment to doing things right.' },
  { year: '2000', title: 'Expanded to Pole Barns', desc: 'Growing demand led us to specialize in post-frame construction, serving farms across Southwest Michigan.' },
  { year: '2010', title: 'Full Renovation Division', desc: 'We formalized our renovation division, bringing full-service remodeling to hundreds of Battle Creek families.' },
  { year: '2024', title: '500+ Projects Completed', desc: 'Three decades later, a trusted name across Michigan with hundreds of completed projects and thousands of satisfied clients.' },
];

const team = [
  {
    name: 'Brian Downs',
    role: 'Founder & Master Builder',
    img: 'https://downsconstructionllc.com/images/aboutimg2a-850w.jpeg',
    bio: 'With over 29 years in the trades, Brian founded Downs Construction on the belief that honest work and fair pricing is all you need to build a great company.',
  },
  {
    name: 'Sarah Downs',
    role: 'Project Manager',
    img: 'https://public.readdy.ai/ai/img_res/d2a39d26635447da7ca9b579c94b3514.jpg',
    bio: 'Sarah keeps every project on track, on budget, and on time. She\'s the voice you hear when things need to happen — and they always do.',
  },
  {
    name: 'Mike Torres',
    role: 'Lead Carpenter',
    img: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20skilled%20male%20carpenter%20tradesperson%20in%20his%2030s%2C%20clean%20work%20shirt%2C%20confident%20friendly%20expression%2C%20neutral%20background%2C%20professional%20headshot%20photo&width=400&height=450&seq=team03&orientation=portrait',
    bio: 'Mike has 15 years of finish carpentry and framing experience. His attention to detail sets the standard for quality across every Downs Construction job site.',
  },
];

function StatCounter({ target, suffix = '', label }: { target: number; suffix?: string; label: string }) {
  const { ref, count } = useCountUp(target);
  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl sm:text-5xl font-black text-white mb-2 leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
        {count}{suffix}
      </div>
      <div className="text-primary-light/70 text-xs font-semibold uppercase tracking-widest">{label}</div>
    </div>
  );
}

export default function AboutPage() {
  const { ref: valRef, visible: valVis } = useScrollAnimation();
  const { ref: milRef, visible: milVis } = useScrollAnimation();
  const { ref: teamRef, visible: teamVis } = useScrollAnimation();

  return (
    <Layout>
      <PageBanner
        title="About Us"
        subtitle="Battle Creek's trusted construction company since 1992. Built on integrity, powered by craftsmanship, and driven by you."
        breadcrumb="About Us"
        imageUrl="https://readdy.ai/api/search-image?query=construction%20team%20standing%20in%20front%20of%20completed%20beautiful%20home%20renovation%20project%2C%20Michigan%20suburban%20neighborhood%2C%20professional%20crew%20in%20work%20clothes%2C%20sunny%20day%2C%20proud%20moment%2C%20quality%20construction%20craftsmanship%20visible&width=1440&height=600&seq=abouthero&orientation=landscape"
        accentWord="About"
      />

      {/* Story */}
      <section className="bg-[#fafaf8] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-0.5 bg-primary" />
              <span className="text-primary text-xs font-bold uppercase tracking-[0.3em]">Our Story</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-gray-900 leading-tight tracking-tight mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Built from the <span className="text-primary">Ground Up</span>
            </h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-5">
              In 1992, Brian Downs started this company with one simple conviction: do excellent work, be completely honest, and charge a fair price. Three decades later, that conviction hasn't changed — it's just been proven right by hundreds of families across Southwest Michigan.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-5">
              We've grown from a one-man operation into a full-service construction company offering renovations, home additions, pole barn construction, and general contracting. But we've never forgotten what got us here: word-of-mouth referrals from clients who trusted us enough to recommend us to their friends.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-8">
              That trust is the foundation of everything at Downs Construction. We earn it one project at a time.
            </p>
            <blockquote className="border-l-4 border-primary pl-5 italic text-gray-700 text-sm sm:text-base">
              "My father taught me that your reputation is everything in this business. A handshake means something. Your word means something. I built this company on that principle."
              <footer className="mt-2 text-primary font-bold text-sm not-italic uppercase tracking-wider">— Brian Downs, Founder</footer>
            </blockquote>
          </div>
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="w-full h-56 sm:h-64 rounded-lg overflow-hidden col-span-2 img-zoom">
                <img src="https://readdy.ai/api/search-image?query=construction%20crew%20working%20on%20residential%20home%20framing%2C%20Michigan%20suburban%20neighborhood%2C%20craftsmen%20using%20tools%2C%20bright%20sunny%20day%2C%20quality%20construction%20site%2C%20professional%20team&width=700&height=300&seq=about01&orientation=landscape" alt="Downs Construction crew working" className="w-full h-full object-cover object-top" />
              </div>
              <div className="w-full h-40 sm:h-48 rounded-lg overflow-hidden img-zoom">
                <img src="https://readdy.ai/api/search-image?query=finished%20beautiful%20home%20exterior%20renovation%20with%20new%20siding%20and%20fresh%20landscaping%2C%20Michigan%20suburban%20house%2C%20before%20and%20after%20transformation%2C%20sunny%20day%2C%20curb%20appeal%20improvement&width=350&height=280&seq=about02&orientation=landscape" alt="Completed renovation project" className="w-full h-full object-cover object-top" />
              </div>
              <div className="w-full h-40 sm:h-48 rounded-lg overflow-hidden img-zoom">
                <img src="https://readdy.ai/api/search-image?query=beautiful%20completed%20home%20addition%20on%20Michigan%20house%2C%20matching%20architecture%2C%20new%20room%20addition%20seamlessly%20integrated%2C%20green%20lawn%2C%20sunny%20afternoon&width=350&height=280&seq=about03&orientation=landscape" alt="Home addition project" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-primary py-14 sm:py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
            <StatCounter target={29} suffix="+" label="Years in Business" />
            <StatCounter target={500} suffix="+" label="Projects Completed" />
            <StatCounter target={100} suffix="%" label="Licensed & Insured" />
            <StatCounter target={4} suffix=".9★" label="Avg. Google Rating" />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#fafaf8] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="text-center mb-12 sm:mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-0.5 bg-primary" />
              <span className="text-primary text-xs font-bold uppercase tracking-[0.3em]">What Drives Us</span>
              <div className="w-8 h-0.5 bg-primary" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-gray-900 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Our Core <span className="text-primary">Values</span>
            </h2>
          </div>
          <div ref={valRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {values.map((val, i) => (
              <div
                key={val.title}
                className="text-center p-6 sm:p-7 bg-white border border-gray-100 rounded-lg hover-card"
                style={{ opacity: valVis ? 1 : 0, transform: valVis ? 'translateY(0)' : 'translateY(32px)', transition: `all 0.7s ease ${i * 120}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center bg-primary rounded-full mx-auto mb-5">
                  <i className={`${val.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 uppercase tracking-wide mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{val.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-primary-dark py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, #2A5BA8 0%, transparent 60%)' }} />
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-16 relative z-10">
          <div className="text-center mb-12 sm:mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-0.5 bg-primary-light" />
              <span className="text-primary-light text-xs font-bold uppercase tracking-[0.3em]">Our History</span>
              <div className="w-8 h-0.5 bg-primary-light" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Three Decades of <span className="text-primary-light">Building</span>
            </h2>
          </div>
          <div ref={milRef} className="relative">
            <div className="absolute left-1/2 -translate-x-px top-0 bottom-0 w-0.5 bg-white/10 hidden md:block" />
            <div className="flex flex-col gap-8 sm:gap-10">
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className={`relative flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  style={{ opacity: milVis ? 1 : 0, transform: milVis ? 'translateX(0)' : `translateX(${i % 2 === 0 ? '-40px' : '40px'})`, transition: `all 0.7s ease ${i * 150}ms` }}
                >
                  <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="inline-block bg-white/5 border border-white/10 rounded-lg p-5 hover-card">
                      <div className="text-primary-light text-3xl font-black mb-1 leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{m.year}</div>
                      <h3 className="text-white font-bold text-lg uppercase tracking-wide mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{m.title}</h3>
                      <p className="text-blue-200/60 text-sm leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-primary-mid rounded-full border-2 border-primary-dark z-10 flex-shrink-0 hidden md:block" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-[#fafaf8] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="text-center mb-12 sm:mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-0.5 bg-primary" />
              <span className="text-primary text-xs font-bold uppercase tracking-[0.3em]">The People Behind The Work</span>
              <div className="w-8 h-0.5 bg-primary" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-gray-900 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Meet the <span className="text-primary">Team</span>
            </h2>
          </div>
          <div ref={teamRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {team.map((member, i) => (
              <div
                key={member.name}
                className="group overflow-hidden rounded-lg bg-white border border-gray-100 hover-card"
                style={{ opacity: teamVis ? 1 : 0, transform: teamVis ? 'translateY(0)' : 'translateY(32px)', transition: `all 0.7s ease ${i * 130}ms` }}
              >
                <div className="w-full h-64 sm:h-72 overflow-hidden img-zoom">
                  <img src={member.img} alt={member.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-5 sm:p-6">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-6 h-0.5 bg-primary flex-shrink-0" />
                    <span className="text-primary text-xs font-bold uppercase tracking-wider">{member.role}</span>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{member.name}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary to-primary-mid">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white mb-4 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Let's Work Together
          </h2>
          <p className="text-blue-100/80 mb-8 text-sm sm:text-base max-w-xl mx-auto">
            Ready to start your project? Get in touch with our team and let's figure out how we can make your vision a reality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-primary-light transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press">
              Contact Us
            </Link>
            <a href="tel:269-599-4913" className="border-2 border-white/60 text-white font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-white/10 transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press">
              (269) 599-4913
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
